import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Header from '../../components/Header';
import MobileNavbar from '../../components/MobileNavbar';
import Sidebar from '../../components/Sidebar';
import CarPhoto from '../../components/CarPhoto';
import './index.css';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { vehicleSelector } from '../../reducers/vehicleSlice';
import { delete_car_image, create_car_image, reorder_images } from '../../API/car_booking';
import { get_car_by_id } from '../../API/index';
import { AuthContext } from '../../contexts/AuthContext';

const MAX_PHOTO_LENGTH = 6;
const EMPTY_IMAGE = { id: 0, image: null };

function getErrorMessage(res, fallback = 'Something went wrong.') {
  const data = res?.response?.data ?? res?.data;

  if (typeof data === 'string' && data.trim()) {
    return data;
  }

  if (data?.detail) {
    return data.detail;
  }

  if (Array.isArray(data) && data.length > 0) {
    return String(data[0]);
  }

  if (data && typeof data === 'object') {
    const firstValue = Object.values(data)[0];
    if (Array.isArray(firstValue) && firstValue.length > 0) {
      return String(firstValue[0]);
    }
    if (firstValue != null) {
      return String(firstValue);
    }
  }

  return fallback;
}

export default function CarPhotos() {
  const { authAxios } = useContext(AuthContext);
  const { car_id } = useParams();
  const { vehicle } = useSelector(vehicleSelector);
  const navigate = useNavigate();
  const [car, setCar] = useState(() => ({
    id: vehicle?.id || 0,
    title: vehicle?.title || vehicle?.vehicleName || '',
    car_images: vehicle?.car_images || [],
  }));
  const [isLoading, setIsLoading] = useState(false);
  const resolvedCarId = car_id || vehicle?.id;

  const refreshCar = useCallback(
    async (nextCarId = resolvedCarId) => {
      if (!nextCarId) {
        return null;
      }

      setIsLoading(true);
      const res = await get_car_by_id(authAxios, nextCarId);

      if (res.status === 200) {
        setCar({
          ...res.data,
          title: res.data.title || res.data.vehicleName || '',
          car_images: res.data.car_images || [],
        });
        setIsLoading(false);
        return res.data;
      }

      setIsLoading(false);
      toast.error(getErrorMessage(res, 'Unable to load vehicle photos.'));
      return null;
    },
    [authAxios, resolvedCarId]
  );

  useEffect(() => {
    if (!resolvedCarId) {
      toast.error('Select a vehicle before editing photos.');
      navigate('/inventory/all_cars');
      return;
    }

    refreshCar(resolvedCarId);
  }, [navigate, refreshCar, resolvedCarId]);

  const moveImage = useCallback(
    async (dragIndex, hoverIndex) => {
      const dragId = car.car_images?.[dragIndex]?.id;
      const hoverId = car.car_images?.[hoverIndex]?.id;

      if (!dragId || !hoverId || dragId === hoverId) {
        return;
      }

      setIsLoading(true);
      const res = await reorder_images(authAxios, {
        car_id: car.id,
        drag_id: dragId,
        hover_id: hoverId,
      });

      if (res.status !== 200) {
        setIsLoading(false);
        toast.error(getErrorMessage(res, 'Unable to reorder photos.'));
        return;
      }

      await refreshCar(car.id);
    },
    [authAxios, car.car_images, car.id, refreshCar]
  );

  const handleSave = async (file, carImage) => {
    if (!file || !car.id) {
      return;
    }

    setIsLoading(true);
    const imageFile = file;
    let res;
    let createdImageId;

    try {
      const formData = new FormData();
      formData.append('car', car.id);
      formData.append('image', imageFile);

      res = await create_car_image(authAxios, formData);

      if (res.status !== 201) {
        setIsLoading(false);
        toast.error(getErrorMessage(res, 'Unable to upload photo.'));
        return;
      }

      createdImageId = res.data?.id;

      if (!carImage?.id || !createdImageId) {
        await refreshCar(car.id);
        return;
      }

      const reorderRes = await reorder_images(authAxios, {
        car_id: car.id,
        drag_id: carImage.id,
        hover_id: createdImageId,
      });

      if (reorderRes.status !== 200) {
        await delete_car_image(authAxios, createdImageId);
        setIsLoading(false);
        toast.error(getErrorMessage(reorderRes, 'Unable to replace photo.'));
        await refreshCar(car.id);
        return;
      }

      const deleteRes = await delete_car_image(authAxios, createdImageId);
      if (deleteRes.status !== 204) {
        setIsLoading(false);
        toast.error(getErrorMessage(deleteRes, 'Photo updated, but cleanup failed.'));
        await refreshCar(car.id);
        return;
      }
    } catch (error) {
      if (createdImageId) {
        await delete_car_image(authAxios, createdImageId);
      }
      setIsLoading(false);
      toast.error(getErrorMessage(error, 'Unable to update photo.'));
      return;
    }

    await refreshCar(car.id);
  };

  const handleDelete = async (e, carImage) => {
    e.preventDefault();
    e.stopPropagation();

    if (!carImage?.id) {
      return;
    }

    setIsLoading(true);
    const res = await delete_car_image(authAxios, carImage.id);

    if (res.status !== 204) {
      setIsLoading(false);
      toast.error(getErrorMessage(res, 'Unable to delete photo.'));
      return;
    }

    await refreshCar(car.id);
  };

  const photoSlots = useMemo(() => {
    const photos = [...(car.car_images || [])];
    while (photos.length < MAX_PHOTO_LENGTH) {
      photos.push({ ...EMPTY_IMAGE, car: car.id });
    }
    return photos;
  }, [car.car_images, car.id]);

  return (
    <div className='dashboard page'>
      <Sidebar page='add_vehicle' />
      <div className='hero_content'>
        <MobileNavbar />
        <Header />
        <div className='editWrapper pagewrapper add_vehicle'>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isLoading}
          >
            <CircularProgress color='inherit' />
          </Backdrop>
          <div className='editContainer'>
            <div className='header'>
              <h5>{car.title}</h5>
              <div className='confirmation_btn'>
                <button onClick={() => navigate('/inventory/all_cars')}>
                  <ArrowBackIcon />
                </button>
              </div>
            </div>
            <form action='#' className='card_details'>
              <div className='info_wrapper photo_wrapper'>
                {photoSlots.map((carImage, index) => (
                  <CarPhoto
                    index={index}
                    key={carImage.id || `empty-${index}`}
                    car_image={carImage}
                    handleSave={handleSave}
                    handleDelete={handleDelete}
                    moveImage={moveImage}
                  />
                ))}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
